import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { useStudioStore } from "@/store/studio-store";
import {
  getStudioCapabilityGatewayRevision,
  listPendingStudioCapabilities,
  subscribeStudioCapabilityGateway,
} from "@/features/chat/services/studio-capability-gateway";
import { deriveCreatorAttention } from "@/features/chat/services/studio-creator-attention";

export function useContextualChatDisclosure(
  attentionRequired = false,
): (open: boolean) => void {
  const initialized = useRef(false);
  const studioPersona = useStudioStore((state) => state.studioPersona);
  const currentProjectId = useStudioStore((state) => state.currentProjectId);
  const dispatchPhase = useStudioStore(
    (state) => state.channelDispatchStatus.phase,
  );
  const setChatSidebarOpen = useStudioStore(
    (state) => state.setChatSidebarOpen,
  );
  const setInspectorPanelOpen = useStudioStore(
    (state) => state.setInspectorPanelOpen,
  );
  useSyncExternalStore(
    subscribeStudioCapabilityGateway,
    getStudioCapabilityGatewayRevision,
    getStudioCapabilityGatewayRevision,
  );
  const pendingConfirmationCount = listPendingStudioCapabilities().filter(
    (pending) => pending.projectId === currentProjectId,
  ).length;

  // Read as state, not a one-shot: the notice can be written by the same turn
  // that mounts this hook (a chat-driven project open), so it must be part of
  // the disclosure decision rather than sampled before it exists.
  const hasPendingNotice = useStudioStore(
    (state) => Boolean(state.pendingChatNotice),
  );

  const needsChat =
    deriveCreatorAttention({
      dispatchPhase,
      unresolvedIntentPreview: attentionRequired,
      pendingConfirmationCount,
      needsClarification: false,
    }) !== null;

  const shouldDisclose = needsChat || hasPendingNotice;

  useEffect(() => {
    if (studioPersona !== "creator") return;

    if (!initialized.current) {
      initialized.current = true;
      // `needsChat` asks "does the system need something FROM the user" —
      // clarification, a terminology choice, a confirmation. A pending notice
      // is the other direction: the assistant has already said something the
      // user has not seen. Closing the drawer on it destroys the only copy.
      //
      // Measured 2026-08-04: opening a project from the dashboard chat moved
      // `chatSidebarOpen` true → false right here, on this hook's first run in
      // the IDE shell — overriding `shell-chat-defaults`, whose own doc says
      // the IDE default is open precisely so nobody asks "why is my chat
      // hidden when I opened a project?". The reply that came with that open
      // was in the notice, and the user never saw it.
      setChatSidebarOpen(shouldDisclose);
    } else if (shouldDisclose) {
      // Also on LATER arrival, not just first run. Measured: the project open
      // mounts this rail before the turn writes its notice, so the first run
      // sees no notice and closes — and without this branch the message that
      // lands a tick later stays hidden behind a collapsed drawer.
      //
      // This does not fight a user who closes the drawer: `hasPendingNotice`
      // has not changed at that point, so the effect does not re-run.
      setChatSidebarOpen(true);
    }

    if (needsChat) {
      setInspectorPanelOpen(false);
    }
  }, [
    shouldDisclose,
    needsChat,
    setChatSidebarOpen,
    setInspectorPanelOpen,
    studioPersona,
  ]);

  return useCallback(
    (open: boolean) => {
      setChatSidebarOpen(open);
      if (open) setInspectorPanelOpen(false);
    },
    [setChatSidebarOpen, setInspectorPanelOpen],
  );
}
